import { customerApiClient } from './customer-api-client';

/** Customer-facing report metadata — only ever returned for an approved &
 * published report the authenticated customer owns (backend-enforced). */
export interface CustomerReportView {
  reportNumber: string;
  status: 'APPROVED';
  equipment: {
    assetNumber: string;
    serialNumber: string;
    modelNumber: string | null;
    name: string | null;
  };
  inspectionDate: string | null;
  generatedDate: string | null;
  approvalDate: string | null;
  /** @deprecated B6.11 (G-1) — renderer build-time constants, not this report's
   * design. Use `layoutVersion`. */
  templateName: string | null;
  /** @deprecated B6.11 (G-1) — see `templateName`. Use `layoutVersion`. */
  templateVersion: string | null;
  /** Version of the layout frozen into this report — what the PDF footer prints. */
  layoutVersion: number | null;
}

/** Reads the server-provided download filename from Content-Disposition. */
function filenameFromDisposition(disposition: string | undefined, fallback: string): string {
  if (!disposition) return fallback;
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  return match?.[1] ?? fallback;
}

/**
 * Customer Portal — Inspection Report (read-only). Downloads stream the PDF
 * bytes through the authenticated customerApiClient (a plain <a href> would not
 * carry the Bearer token); the server never hands out a reusable URL and never
 * changes report state. PDFs are never generated or cached in the browser.
 */
export const customerReportService = {
  getReport: (jobId: string): Promise<CustomerReportView> =>
    customerApiClient
      .get<CustomerReportView>(`/customer-portal/jobs/${jobId}/report`)
      .then((r) => r.data),

  async download(jobId: string): Promise<void> {
    const response = await customerApiClient.get(`/customer-portal/jobs/${jobId}/report/pdf`, {
      responseType: 'blob',
    });
    const filename = filenameFromDisposition(
      response.headers?.['content-disposition'] as string | undefined,
      'inspection-report.pdf',
    );
    const url = URL.createObjectURL(response.data as Blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  },
};
